/**
 * Runtime Configuration Loader
 *
 * This module provides runtime configuration loading capabilities to support
 * the "Build Once, Deploy Many" deployment strategy. Instead of baking environment
 * configuration into the build artifact, we load it at runtime from a JSON file.
 *
 * Benefits:
 * - Single build artifact used across all environments (DEV, QA, SBX, PRD)
 * - True artifact promotion - exact code tested in lower environments goes to production
 * - Faster deployments - no rebuild required for upper environments
 * - Eliminates configuration drift
 *
 * @module runtimeConfig
 */

/**
 * Runtime configuration structure matching Vite environment variables
 */
export interface RuntimeConfig {
    env: string;
    VITE_ENV: string;
    VITE_IDENTITY_HOST: string;
    VITE_AVATAX_API_HOST: string;
    VITE_CUP_HOST: string;
    VITE_API_HOST_BASE: string;
    VITE_DOCLOCKER_HOST: string;
    VITE_CERTCAPTURE: string;
    VITE_GENCERT_URL: string;
    VITE_GENCERT_API_URL: string;
    VITE_CERTEXPRESS_URL: string;
    VITE_API_VERSION: string;
    VITE_API_VERSION3: string;
    VITE_DATASERVICE_URL: string;
    /** AvaApp shell origin (Global Imports); mirrors AvaAppConfig.hostnames.avaApp */
    VITE_AVA_APP_HOST?: string;
    /** When `"true"`, allow navigation to AvaApp Global Imports (default off if missing or not `"true"`) */
    VITE_ENABLE_GLOBAL_IMPORTS?: string;
    VITE_IPUA?: string;
}

/**
 * Cached runtime configuration
 */
let cachedConfig: RuntimeConfig | null = null;

/**
 * Flag to track if configuration is currently being loaded
 */
let isLoading = false;

/**
 * Promise for ongoing configuration load
 */
let loadPromise: Promise<RuntimeConfig> | null = null;

/**
 * Load runtime configuration from /config.json
 *
 * This function fetches the configuration file with cache-busting to ensure
 * fresh config after deployments. The configuration is cached in memory after
 * the first successful load.
 *
 * @returns Promise resolving to the runtime configuration
 * @throws Error if configuration fails to load or is invalid
 *
 * @example
 * ```typescript
 * try {
 *   const config = await loadRuntimeConfig();
 *   console.log('Environment:', config.env);
 * } catch (error) {
 *   console.error('Failed to load configuration:', error);
 * }
 * ```
 */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
    // Return cached config if available
    if (cachedConfig) {
        return cachedConfig;
    }

    // Return existing load promise if already loading
    if (isLoading && loadPromise) {
        return loadPromise;
    }

    // Start loading
    isLoading = true;
    loadPromise = (async () => {
        try {
            // Add cache-busting query parameter to prevent stale configs
            const timestamp = Date.now();
            const response = await fetch(`/config.json?v=${timestamp}`);

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch runtime config: ${response.status} ${response.statusText}`
                );
            }

            const config = (await response.json()) as RuntimeConfig;

            // Validate that essential fields are present
            if (!config.env || !config.VITE_ENV) {
                throw new Error("Invalid runtime config: missing required fields (env, VITE_ENV)");
            }

            // Cache the configuration
            cachedConfig = config;

            // eslint-disable-next-line no-console
            console.info(
                `✅ Runtime configuration loaded successfully for environment: ${config.env}`
            );

            return config;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("❌ Failed to load runtime configuration:", error);
            throw error;
        } finally {
            isLoading = false;
            loadPromise = null;
        }
    })();

    return loadPromise;
}

/**
 * Get a specific configuration value by key
 *
 * This is a synchronous function that requires configuration to be loaded first.
 * Use after calling loadRuntimeConfig() during app initialization.
 *
 * @param key - The configuration key to retrieve
 * @returns The configuration value, or empty string if not found
 *
 * @example
 * ```typescript
 * const apiHost = getRuntimeConfigValue('VITE_API_HOST_BASE');
 * ```
 */
export function getRuntimeConfigValue(key: keyof RuntimeConfig): string {
    if (!cachedConfig) {
        // eslint-disable-next-line no-console
        console.warn("Runtime config not loaded yet. Call loadRuntimeConfig() first.");
        return "";
    }

    return cachedConfig[key] || "";
}

/**
 * Get the entire runtime configuration
 *
 * This is a synchronous function that requires configuration to be loaded first.
 * Use after calling loadRuntimeConfig() during app initialization.
 *
 * @returns The complete runtime configuration object, or null if not loaded
 *
 * @example
 * ```typescript
 * const config = getRuntimeConfig();
 * if (config) {
 *   console.log('All config:', config);
 * }
 * ```
 */
export function getRuntimeConfig(): RuntimeConfig | null {
    return cachedConfig;
}

/**
 * Clear the cached configuration
 *
 * Useful for testing or forcing a config reload.
 * Not typically needed in production code.
 */
export function clearRuntimeConfig(): void {
    cachedConfig = null;
    isLoading = false;
    loadPromise = null;
}
